import { FC, ReactNode, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  // Shown after the input, for example a results count.
  trailing?: ReactNode;
  className?: string;
}

// The search row at the top of a picker panel. It takes focus when it
// appears, but only after the panel has been placed and without scrolling the
// page: a plain `autoFocus` fires before Floating UI has positioned the panel,
// so the browser would scroll to wherever the panel sat at that instant and
// leave it floating in the wrong place.
export const SearchField: FC<Props> = ({
  value,
  onChange,
  placeholder,
  trailing,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div className={classNames(styles.row, className)}>
      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        className={styles.icon}
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={styles.input}
      />
      {trailing}
    </div>
  );
};

const styles = {
  row: 'flex items-center gap-2 border-b border-hairline px-3.5 py-3',
  icon: 'text-[13px] text-ink-tertiary',
  input: classNames(
    'block min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-ink-primary',
    'placeholder-ink-tertiary focus:outline-hidden focus:ring-0',
  ),
};
